import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { HeaderPro } from '../../components/HeaderPro';
import { Footer } from '../../components/Footer';
import {
  cancelPaymentRequest,
  createPaymentRequest,
  cancelPatchTest,
  proposePatchTest,
  getMessageThread,
  listMessageThreads,
  markThreadRead,
  sendMessage,
  uploadMessageImage,
  type ChatMessage,
  type MessageThread,
} from '../../../lib/messaging-api';
import { PaymentRequestBubble } from '../../components/PaymentRequestBubble';
import { PatchTestBubble } from '../../components/PatchTestBubble';
import { useAuth } from '../../../lib/use-auth';
import { apiErrorMessage } from '../../../lib/api';
import { ukSlotToISO } from '../../../lib/uk-time';
import { toast } from '../../../lib/toast';
import {
  notifyMessagesChanged,
  useMessageStream,
} from '../../../lib/use-message-stream';

// Poll the active thread + the inbox while the page is open. Five
// seconds is the same balance the customer side uses — fresh enough
// to feel live without burning a request per second.
const POLL_MS = 5000;

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

function formatDay(iso: string): string {
  try {
    const d = new Date(iso);
    const today = new Date();
    if (d.toDateString() === today.toDateString()) return 'Today';
    const y = new Date(today);
    y.setDate(today.getDate() - 1);
    if (d.toDateString() === y.toDateString()) return 'Yesterday';
    return d.toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
    });
  } catch {
    return '';
  }
}

function displayName(t: MessageThread['counterparty']): string {
  return (
    [t.first_name, t.last_name].filter(Boolean).join(' ') || 'Unknown user'
  );
}

export default function MessengerPro() {
  const { user } = useAuth();
  const myUserId = user?.id ?? null;
  const location = useLocation();
  // Pages can deep-link into a specific thread by navigating with
  // `state: { openWithUserId }` — e.g. the "Message customer" button
  // on a booking detail page. Read once on mount; we set this as the
  // active thread below.
  const initialOtherId =
    (location.state as { openWithUserId?: string } | null)?.openWithUserId ??
    null;

  const [threads, setThreads] = useState<MessageThread[]>([]);
  const [threadsLoading, setThreadsLoading] = useState(true);

  const [activeOtherId, setActiveOtherId] = useState<string | null>(
    initialOtherId,
  );
  const [activeCounterparty, setActiveCounterparty] =
    useState<MessageThread['counterparty'] | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);

  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);

  // In-chat payment request (pro asks the client to pay for an extra).
  const [prOpen, setPrOpen] = useState(false);
  const [prAmount, setPrAmount] = useState('');
  const [prDesc, setPrDesc] = useState('');
  const [prBusy, setPrBusy] = useState(false);
  const [cancelingId, setCancelingId] = useState<string | null>(null);
  // Patch-test proposal form.
  const [ptOpen, setPtOpen] = useState(false);
  const [ptDate, setPtDate] = useState('');
  const [ptTime, setPtTime] = useState('10:00');
  const [ptDuration, setPtDuration] = useState('15');
  const [ptNote, setPtNote] = useState('');
  const [ptBusy, setPtBusy] = useState(false);
  const [ptCancelingId, setPtCancelingId] = useState<string | null>(null);

  const listRef = useRef<HTMLDivElement>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);

  const loadThreads = useCallback(async () => {
    try {
      const res = await listMessageThreads('pro');
      setThreads(res.data ?? []);
    } catch (err) {
      const msg = apiErrorMessage(err, 'Could not load conversations.');
      toast.error(msg);
    } finally {
      setThreadsLoading(false);
    }
  }, []);

  const loadThread = useCallback(async (otherId: string) => {
    try {
      const res = await getMessageThread('pro', otherId);
      setActiveCounterparty(res.counterparty);
      setMessages(res.data ?? []);
    } catch (err) {
      const msg = apiErrorMessage(err, 'Could not load the conversation.');
      toast.error(msg);
    } finally {
      setMessagesLoading(false);
    }
  }, []);

  // First mount: pull threads. Re-poll on the same interval.
  useEffect(() => {
    void loadThreads();
    const id = window.setInterval(loadThreads, POLL_MS);
    return () => window.clearInterval(id);
  }, [loadThreads]);

  // Active thread: poll for new messages + always scroll to bottom on
  // refresh. Resetting the active id resets the message list so the
  // previous thread's bubbles don't flash through.
  useEffect(() => {
    if (!activeOtherId) {
      setMessages([]);
      setActiveCounterparty(null);
      return;
    }
    setMessagesLoading(true);
    void loadThread(activeOtherId);
    const id = window.setInterval(() => {
      void loadThread(activeOtherId);
    }, POLL_MS);
    return () => window.clearInterval(id);
  }, [activeOtherId, loadThread]);

  // Live updates via SSE — refresh the inbox instantly on any new
  // message, and the open conversation when it's from the active
  // counterparty (the 5s poll remains the fallback).
  useMessageStream('pro', (m) => {
    void loadThreads();
    if (m.sender_user_id === activeOtherId) {
      void loadThread(activeOtherId);
      void markThreadRead('pro', activeOtherId).catch(() => {});
    }
  });

  useEffect(() => {
    listRef.current?.scrollTo({
      top: listRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [messages.length]);

  const sortedThreads = useMemo(() => {
    return threads
      .slice()
      .sort(
        (a, b) =>
          new Date(b.last_message.created_at).getTime() -
          new Date(a.last_message.created_at).getTime(),
      );
  }, [threads]);

  async function handleSendImage(file: File) {
    if (!activeOtherId || uploadingImage) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file.');
      return;
    }
    setUploadingImage(true);
    try {
      const up = await uploadMessageImage('pro', file);
      await sendMessage('pro', {
        recipient_user_id: activeOtherId,
        body: '',
        message_type: 'IMAGE',
        attachment_url: up.url,
      });
      await Promise.all([loadThread(activeOtherId), loadThreads()]);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not send that photo.'));
    } finally {
      setUploadingImage(false);
    }
  }

  async function handleSend() {
    if (!activeOtherId || !draft.trim() || sending) return;
    const body = draft.trim();
    setSending(true);
    // Optimistic insert so the textbox feels responsive — the next
    // poll replaces it with the canonical row from the server.
    const optimistic: ChatMessage = {
      id: `temp-${Date.now()}`,
      body,
      sender_user_id: myUserId ?? 'me',
      recipient_user_id: activeOtherId,
      booking_id: null,
      created_at: new Date().toISOString(),
      read_at: null,
    };
    setMessages((prev) => [...prev, optimistic]);
    setDraft('');
    try {
      await sendMessage('pro', {
        recipient_user_id: activeOtherId,
        body,
      });
      await Promise.all([loadThread(activeOtherId), loadThreads()]);
    } catch (err) {
      const msg = apiErrorMessage(err, 'Could not send that message.');
      toast.error(msg);
      // Roll back the optimistic insert on failure so the user can
      // see the textbox is still theirs.
      setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
      setDraft(body);
    } finally {
      setSending(false);
    }
  }

  async function handleCreateRequest() {
    if (!activeOtherId || prBusy) return;
    const amount = Number(prAmount);
    const description = prDesc.trim();
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error('Enter a valid amount.');
      return;
    }
    if (!description) {
      toast.error('Add a short description.');
      return;
    }
    setPrBusy(true);
    try {
      // Link to the thread's booking when there is one.
      const bookingId =
        threads.find((t) => t.counterparty.id === activeOtherId)?.booking_id ??
        null;
      await createPaymentRequest('pro', {
        recipient_user_id: activeOtherId,
        amount,
        description,
        booking_id: bookingId,
      });
      setPrOpen(false);
      setPrAmount('');
      setPrDesc('');
      await Promise.all([loadThread(activeOtherId), loadThreads()]);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not send the payment request.'));
    } finally {
      setPrBusy(false);
    }
  }

  async function handleCancelRequest(requestId: string) {
    if (cancelingId) return;
    setCancelingId(requestId);
    try {
      await cancelPaymentRequest('pro', requestId);
      if (activeOtherId) await loadThread(activeOtherId);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not cancel the request.'));
    } finally {
      setCancelingId(null);
    }
  }

  async function handleProposePatchTest() {
    if (ptBusy || !activeOtherId) return;
    if (!ptDate || !ptTime) {
      toast.error('Pick a date and time for the patch test.');
      return;
    }
    // Interpret the picked time as UK (Europe/London) wall-clock regardless of
    // the pro's browser timezone — the booking system is UK-based. Parsing it
    // as browser-local stored the wrong instant (e.g. from a non-UK browser a
    // 10:00 pick was saved as 10:00 in the browser's zone), so the customer
    // and the booking ended up at a different time than the pro selected.
    const [py, pm, pd] = ptDate.split('-').map(Number);
    const scheduledIso = ukSlotToISO(new Date(py, pm - 1, pd), ptTime);
    const scheduled = new Date(scheduledIso);
    if (Number.isNaN(scheduled.getTime()) || scheduled.getTime() <= Date.now()) {
      toast.error('Pick a future date and time.');
      return;
    }
    setPtBusy(true);
    try {
      const bookingId =
        threads.find((t) => t.counterparty.id === activeOtherId)?.booking_id ??
        null;
      await proposePatchTest('pro', {
        recipient_user_id: activeOtherId,
        scheduled_start: scheduledIso,
        duration_minutes: Math.max(5, parseInt(ptDuration, 10) || 15),
        booking_id: bookingId,
        note: ptNote.trim() || null,
      });
      setPtOpen(false);
      setPtDate('');
      setPtNote('');
      await Promise.all([loadThread(activeOtherId), loadThreads()]);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not send the patch test request.'));
    } finally {
      setPtBusy(false);
    }
  }

  async function handleCancelPatchTest(id: string) {
    if (ptCancelingId) return;
    setPtCancelingId(id);
    try {
      await cancelPatchTest('pro', id);
      if (activeOtherId) await loadThread(activeOtherId);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not cancel the patch test.'));
    } finally {
      setPtCancelingId(null);
    }
  }

  function handleOpenThread(otherId: string) {
    setActiveOtherId(otherId);
    // Clear local unread badge eagerly; the server already marked
    // them read inside getThread but our threads list polls behind.
    setThreads((prev) =>
      prev.map((t) =>
        t.counterparty.id === otherId ? { ...t, unread_count: 0 } : t,
      ),
    );
    void markThreadRead('pro', otherId)
      // Clear the header message badge straight away once read.
      .then(() => notifyMessagesChanged())
      .catch(() => {});
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <HeaderPro />

      <main className="flex-1 max-w-[1140px] mx-auto w-full px-4 py-6 md:py-12">
        <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[24px] mb-6">
          Messages
        </h1>

        <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] gap-6 min-h-[520px]">
          {/* Threads pane */}
          <aside className="border border-[#ede8e9] rounded-xl bg-white overflow-hidden flex flex-col">
            <header className="px-4 py-3 border-b border-[#ede8e9] flex items-center justify-between">
              <span className="font-bold text-[#3a3335] text-[14px]">
                Conversations
              </span>
              <span className="text-[12px] text-[#908e8e]">
                {threads.length}
              </span>
            </header>

            <div className="flex-1 overflow-y-auto">
              {threadsLoading ? (
                <p className="px-4 py-6 text-sm text-[#908e8e]">Loading…</p>
              ) : sortedThreads.length === 0 ? (
                <p className="px-4 py-6 text-sm text-[#908e8e]">
                  No conversations yet. They'll appear here once a customer
                  messages you.
                </p>
              ) : (
                <ul className="divide-y divide-[#ede8e9]">
                  {sortedThreads.map((t) => {
                    const isActive = activeOtherId === t.counterparty.id;
                    const lastFromMe =
                      t.last_message.sender_user_id === myUserId;
                    return (
                      <li
                        key={t.counterparty.id}
                        onClick={() => handleOpenThread(t.counterparty.id)}
                        className={`px-4 py-3 cursor-pointer transition-colors ${
                          isActive
                            ? 'bg-[#fbeaea]'
                            : 'hover:bg-[#fcfaf7]'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-full bg-[#f2eee2] flex items-center justify-center overflow-hidden shrink-0">
                            {t.counterparty.profile_pic_preview ??
                            t.counterparty.profile_pic ? (
                              <img
                                src={
                                  t.counterparty.profile_pic_preview ??
                                  t.counterparty.profile_pic ??
                                  ''
                                }
                                alt={displayName(t.counterparty)}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <span className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[14px]">
                                {displayName(t.counterparty)
                                  .slice(0, 1)
                                  .toUpperCase()}
                              </span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-bold text-[#3a3335] text-[14px] truncate">
                                {displayName(t.counterparty)}
                              </span>
                              <span className="text-[11px] text-[#908e8e] shrink-0">
                                {formatDay(t.last_message.created_at)}
                              </span>
                            </div>
                            <p className="text-[12px] text-[#908e8e] truncate">
                              {lastFromMe ? 'You: ' : ''}
                              {t.last_message.body}
                            </p>
                          </div>
                          {t.unread_count > 0 && !isActive ? (
                            <span className="bg-[#8e2a2f] text-white text-[11px] font-bold rounded-full px-2 py-0.5 shrink-0">
                              {t.unread_count}
                            </span>
                          ) : null}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </aside>

          {/* Conversation pane */}
          <section className="border border-[#ede8e9] rounded-xl bg-white flex flex-col min-h-[520px]">
            {!activeOtherId ? (
              <div className="flex-1 flex items-center justify-center text-sm text-[#908e8e] px-6 text-center">
                Pick a conversation on the left to start replying.
              </div>
            ) : (
              <>
                <header className="px-4 py-3 border-b border-[#ede8e9] flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-[#f2eee2] flex items-center justify-center overflow-hidden">
                    {activeCounterparty?.profile_pic_preview ??
                    activeCounterparty?.profile_pic ? (
                      <img
                        src={
                          activeCounterparty?.profile_pic_preview ??
                          activeCounterparty?.profile_pic ??
                          ''
                        }
                        alt={displayName(activeCounterparty)}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[13px]">
                        {activeCounterparty
                          ? displayName(activeCounterparty)
                              .slice(0, 1)
                              .toUpperCase()
                          : '?'}
                      </span>
                    )}
                  </div>
                  <span className="font-bold text-[#3a3335] text-[14px]">
                    {activeCounterparty
                      ? displayName(activeCounterparty)
                      : 'Conversation'}
                  </span>
                </header>

                <div
                  ref={listRef}
                  className="flex-1 overflow-y-auto px-4 py-4 space-y-2 bg-[#fcfaf7]"
                >
                  {messagesLoading && messages.length === 0 ? (
                    <p className="text-sm text-[#908e8e]">Loading…</p>
                  ) : messages.length === 0 ? (
                    <p className="text-sm text-[#908e8e]">
                      No messages yet. Say hello!
                    </p>
                  ) : (
                    messages.map((m) => {
                      const fromMe = m.sender_user_id === myUserId;
                      if (
                        m.message_type === 'PAYMENT_REQUEST' &&
                        m.payment_request
                      ) {
                        return (
                          <div
                            key={m.id}
                            className={`flex ${fromMe ? 'justify-end' : 'justify-start'}`}
                          >
                            <PaymentRequestBubble
                              pr={m.payment_request}
                              fromMe={fromMe}
                              canPay={false}
                              onCancel={() =>
                                handleCancelRequest(m.payment_request!.id)
                              }
                              busy={cancelingId === m.payment_request.id}
                            />
                          </div>
                        );
                      }
                      if (
                        m.message_type === 'PATCH_TEST_REQUEST' &&
                        m.patch_test
                      ) {
                        return (
                          <div
                            key={m.id}
                            className={`flex ${fromMe ? 'justify-end' : 'justify-start'}`}
                          >
                            <PatchTestBubble
                              pt={m.patch_test}
                              fromMe={fromMe}
                              canRespond={false}
                              onCancel={() =>
                                handleCancelPatchTest(m.patch_test!.id)
                              }
                              busy={ptCancelingId === m.patch_test.id}
                            />
                          </div>
                        );
                      }
                      if (m.message_type === 'IMAGE' && m.attachment_url) {
                        return (
                          <div
                            key={m.id}
                            className={`flex ${fromMe ? 'justify-end' : 'justify-start'}`}
                          >
                            <div
                              className={`max-w-[75%] overflow-hidden rounded-2xl ${
                                fromMe
                                  ? 'bg-[#8e2a2f]'
                                  : 'bg-white border border-[#ede8e9]'
                              }`}
                            >
                              <a
                                href={m.attachment_url}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <img
                                  src={m.attachment_url}
                                  alt="Shared photo"
                                  className="max-h-[260px] w-auto object-cover"
                                />
                              </a>
                              {m.body ? (
                                <p
                                  className={`px-3 py-1.5 text-[13px] ${fromMe ? 'text-white' : 'text-[#3a3335]'}`}
                                >
                                  {m.body}
                                </p>
                              ) : null}
                              <p
                                className={`px-3 pb-1.5 text-[10px] ${fromMe ? 'text-white/70' : 'text-[#908e8e]'} text-right`}
                              >
                                {formatTime(m.created_at)}
                              </p>
                            </div>
                          </div>
                        );
                      }
                      return (
                        <div
                          key={m.id}
                          className={`flex ${fromMe ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-[75%] rounded-2xl px-4 py-2 text-[14px] leading-[20px] ${
                              fromMe
                                ? 'bg-[#8e2a2f] text-white rounded-br-sm'
                                : 'bg-white text-[#3a3335] border border-[#ede8e9] rounded-bl-sm'
                            }`}
                          >
                            <p className="whitespace-pre-wrap break-words">
                              {m.body}
                            </p>
                            <p
                              className={`mt-1 text-[10px] ${fromMe ? 'text-white/70' : 'text-[#908e8e]'} text-right`}
                            >
                              {formatTime(m.created_at)}
                            </p>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                <footer className="px-4 py-3 border-t border-[#ede8e9] bg-white">
                  {prOpen ? (
                    <div className="mb-3 rounded-[12px] border border-[#ede8e9] bg-[#fcfaf7] p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-[13px] font-bold text-[#3a3335]">
                          Request a payment
                        </p>
                        <button
                          type="button"
                          onClick={() => setPrOpen(false)}
                          className="text-[#908e8e] hover:text-[#3a3335]"
                        >
                          <span className="material-icons text-[18px]">close</span>
                        </button>
                      </div>
                      <div className="flex gap-2">
                        <div className="relative w-[110px]">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[14px] font-bold text-[#3a3335] pointer-events-none">
                            £
                          </span>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={prAmount}
                            onChange={(e) =>
                              /^[0-9]*\.?[0-9]{0,2}$/.test(e.target.value) ||
                              e.target.value === ''
                                ? setPrAmount(e.target.value)
                                : undefined
                            }
                            placeholder="15"
                            className="w-full h-[40px] pl-7 pr-2 rounded-[8px] border border-[#ede8e9] bg-white text-[14px] font-bold text-[#3a3335] outline-none focus:border-[#8e2a2f]"
                          />
                        </div>
                        <input
                          type="text"
                          value={prDesc}
                          onChange={(e) => setPrDesc(e.target.value)}
                          placeholder="e.g. Olaplex treatment"
                          maxLength={200}
                          className="flex-1 h-[40px] px-3 rounded-[8px] border border-[#ede8e9] bg-white text-[14px] text-[#3a3335] outline-none focus:border-[#8e2a2f]"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => void handleCreateRequest()}
                        disabled={prBusy}
                        className="w-full h-[40px] rounded-[8px] bg-[#8e2a2f] text-white text-[14px] font-bold hover:bg-[#7a2228] transition-colors disabled:opacity-60"
                      >
                        {prBusy ? 'Sending…' : 'Send payment request'}
                      </button>
                      <p className="text-[11px] text-[#908e8e]">
                        The client pays your rate plus the platform fee and VAT
                        through Cherry Picked. Your payout is your rate less our
                        commission — the exact figure shows on the request once
                        sent.
                      </p>
                    </div>
                  ) : null}
                  {ptOpen ? (
                    <div className="mb-3 rounded-[12px] border border-[#ede8e9] bg-[#fcfaf7] p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-[13px] font-bold text-[#3a3335]">
                          Propose a patch test
                        </p>
                        <button
                          type="button"
                          onClick={() => setPtOpen(false)}
                          className="text-[#908e8e] hover:text-[#3a3335]"
                        >
                          <span className="material-icons text-[18px]">close</span>
                        </button>
                      </div>
                      <div className="flex gap-2">
                        <input
                          type="date"
                          value={ptDate}
                          onChange={(e) => setPtDate(e.target.value)}
                          className="flex-1 h-[40px] px-2 rounded-[8px] border border-[#ede8e9] bg-white text-[14px] text-[#3a3335] outline-none focus:border-[#8e2a2f]"
                        />
                        <input
                          type="time"
                          value={ptTime}
                          onChange={(e) => setPtTime(e.target.value)}
                          className="w-[108px] h-[40px] px-2 rounded-[8px] border border-[#ede8e9] bg-white text-[14px] text-[#3a3335] outline-none focus:border-[#8e2a2f]"
                        />
                        <div className="relative w-[88px]">
                          <input
                            type="text"
                            inputMode="numeric"
                            value={ptDuration}
                            onChange={(e) =>
                              /^[0-9]*$/.test(e.target.value)
                                ? setPtDuration(e.target.value)
                                : undefined
                            }
                            placeholder="15"
                            className="w-full h-[40px] pl-2 pr-8 rounded-[8px] border border-[#ede8e9] bg-white text-[14px] text-[#3a3335] outline-none focus:border-[#8e2a2f]"
                          />
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[12px] text-[#908e8e] pointer-events-none">
                            min
                          </span>
                        </div>
                      </div>
                      <input
                        type="text"
                        value={ptNote}
                        onChange={(e) => setPtNote(e.target.value)}
                        maxLength={300}
                        placeholder="Optional note (e.g. before your colour appointment)"
                        className="w-full h-[40px] px-3 rounded-[8px] border border-[#ede8e9] bg-white text-[14px] text-[#3a3335] outline-none focus:border-[#8e2a2f]"
                      />
                      <button
                        type="button"
                        onClick={() => void handleProposePatchTest()}
                        disabled={ptBusy}
                        className="w-full h-[40px] rounded-[8px] bg-[#8e2a2f] text-white text-[14px] font-bold hover:bg-[#7a2228] transition-colors disabled:opacity-60"
                      >
                        {ptBusy ? 'Sending…' : 'Send patch test request'}
                      </button>
                      <p className="text-[11px] text-[#908e8e]">
                        The client confirms or declines. Once confirmed it's
                        added to both calendars and blocks that time.
                      </p>
                    </div>
                  ) : null}
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      void handleSend();
                    }}
                    className="flex items-center gap-2"
                  >
                    <button
                      type="button"
                      onClick={() => setPrOpen((v) => !v)}
                      title="Request payment"
                      className="shrink-0 w-10 h-10 rounded-full border border-[#ede8e9] text-[#8e2a2f] flex items-center justify-center hover:bg-[#fbeaea] transition-colors"
                    >
                      <span className="material-icons text-[20px]">
                        request_quote
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setPtOpen((v) => !v)}
                      title="Propose patch test"
                      className="shrink-0 w-10 h-10 rounded-full border border-[#ede8e9] text-[#8e2a2f] flex items-center justify-center hover:bg-[#fbeaea] transition-colors"
                    >
                      <span className="material-icons text-[20px]">
                        event_available
                      </span>
                    </button>
                    <input
                      ref={imageInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) void handleSendImage(f);
                        e.target.value = '';
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => imageInputRef.current?.click()}
                      disabled={uploadingImage || sending}
                      title="Send a photo"
                      aria-label="Send a photo"
                      className="shrink-0 w-10 h-10 rounded-full border border-[#ede8e9] text-[#8e2a2f] flex items-center justify-center hover:bg-[#fbeaea] transition-colors disabled:opacity-50"
                    >
                      <span className="material-icons text-[20px]">
                        {uploadingImage ? 'hourglass_empty' : 'image'}
                      </span>
                    </button>
                    <input
                      type="text"
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      placeholder="Type a message…"
                      className="flex-1 bg-[#fcfaf7] border border-[#ede8e9] rounded-full px-4 py-2 text-[14px] outline-none focus:border-[#8e2a2f]"
                    />
                    <button
                      type="submit"
                      disabled={!draft.trim() || sending}
                      className="bg-[#8e2a2f] text-white rounded-full w-10 h-10 flex items-center justify-center disabled:opacity-50"
                    >
                      <span className="material-icons text-[20px]">send</span>
                    </button>
                  </form>
                </footer>
              </>
            )}
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
